import { AppointmentListCard } from "@/features/appointments/components/AppointmentListCard";
import { getClientName } from "@/features/clients/utils/clientDisplay";
import { ServiceRow } from "@/features/services/components/ServiceRow";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { ItemList } from "@/shared/components/ui/ItemList";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import type { SearchResponse } from "../types/searchTypes";

function matchCount(count: number, hasMore: boolean) {
  return `${count}${hasMore ? "+" : ""} match${count === 1 && !hasMore ? "" : "es"}`;
}

function MoreMatches({ category }: { category: string }) {
  return (
    <p className="text-xs leading-relaxed text-muted-foreground">
      More {category} match. Refine your search to narrow the results.
    </p>
  );
}

export function SearchResults({
  data,
  query,
}: {
  data: SearchResponse;
  query: string;
}) {
  const { goToClientDetails, goToAppointmentDetails, goToServiceDetails } = useAppNavigation();

  return (
    <>
      <Section title="Customers" count={matchCount(data.clients.length, data.hasMore.clients)}>
        <ItemList
          items={data.clients}
          emptyState={{ message: "No customers match this search." }}
          renderItem={(client) => (
            <InfoRow
              title={getClientName(client)}
              subtitle={client.email}
              caption={client.phoneNumber}
              searchText={query}
              density="compact"
              onClick={() => goToClientDetails(client.id)}
            />
          )}
        />
        {data.hasMore.clients && <MoreMatches category="customers" />}
      </Section>
      <Section title="Appointments" count={matchCount(data.appointments.length, data.hasMore.appointments)}>
        <ItemList
          items={data.appointments}
          emptyState={{ message: "No appointments match this search." }}
          renderItem={(appointment) => (
            <AppointmentListCard
              appointment={appointment}
              client={appointment.client}
              searchText={query}
              showDate
              showPrice
              onClick={() => goToAppointmentDetails(appointment.id, false)}
            />
          )}
        />
        {data.hasMore.appointments && <MoreMatches category="appointments" />}
      </Section>
      <Section title="Services" count={matchCount(data.services.length, data.hasMore.services)}>
        <ItemList
          items={data.services}
          emptyState={{ message: "No services match this search." }}
          renderItem={(service) => (
            <ServiceRow
              service={service}
              searchText={query}
              showDescription
              onClick={() => goToServiceDetails(service.id)}
            />
          )}
        />
        {data.hasMore.services && <MoreMatches category="services" />}
      </Section>
    </>
  );
}
